/** Outstanding snag tallies for the workspaces of the projects on screen, keyed by project id. The route leaves out a
 *  project with nothing outstanding, so `loaded` names the workspaces that answered: a project in one of them with no
 *  tally has none. Any failure (no access, table not there yet) leaves that workspace out of `loaded`. */

import { useEffect, useMemo, useState } from 'react';
import { ProjectSnagService, type OutstandingSnagTally } from '../../services/projectSnagService';

export interface OutstandingSnags { tallies: Map<string, OutstandingSnagTally>; loaded: Set<string> }

export function useOutstandingSnags(workspaceIds: readonly string[], enabled: boolean): OutstandingSnags {
  const key = useMemo(() => [...new Set(workspaceIds)].sort().join(','), [workspaceIds]);
  const [loaded, setLoaded] = useState<{ key: string; snags: OutstandingSnags } | null>(null);

  useEffect(() => {
    if (!enabled || !key) return;
    let cancelled = false;
    Promise.all(key.split(',').map((id) => ProjectSnagService.outstanding(id).catch(() => null))).then((all) => {
      if (cancelled) return;
      const tallies = new Map<string, OutstandingSnagTally>();
      const answered = new Set<string>();
      all.forEach((counts, i) => {
        if (!counts) return;
        answered.add(key.split(',')[i]);
        for (const [projectId, tally] of Object.entries(counts)) tallies.set(projectId, tally);
      });
      setLoaded({ key, snags: { tallies, loaded: answered } });
    });
    return () => { cancelled = true; };
  }, [enabled, key]);

  return enabled && loaded?.key === key ? loaded.snags : EMPTY;
}

const EMPTY: OutstandingSnags = { tallies: new Map(), loaded: new Set() };
